import type {
  FinancialBillingState,
  FinancialBilledSnapshot,
  FinancialCharge,
  FinancialImport,
  FinancialRowSignals,
  FinancialRowWarning,
  FinancialServiceMapping,
  FinancialShipmentEntry,
  FinancialShipmentIndex,
} from '@/types/Models/Financial'
import { FINANCIAL_ARREARS_FROM } from '@/const/Financial'
import { financialAmountLabel, financialAmountsEqual, subtractFinancialAmounts } from '@/services/FinancialAmounts'
import {
  financialBookedComparison,
  financialServiceComparison,
  financialWeightComparison,
  financialWeightGrams,
} from '@/services/FinancialComparison'
import {
  financialBillingId,
  financialChargeAmount,
  financialInvoiceLabel,
  financialLocalDate,
  financialMetadataComplete,
  financialRole,
} from '@/services/FinancialImport'
import { financialLoss } from '@/services/FinancialPricing'

const billingCache = new WeakMap<FinancialImport, Map<string, FinancialBillingState>>()

export const financialShipmentKey = (carrier: string, trackingNumber: string): string => {
  return `${carrier}:${trackingNumber.trim().toUpperCase()}`
}

// Billing state per row from the export history. A row stays billed only while its approved amount and billing
// customer equal what was billed; otherwise it changed after billing and needs a decision (difference or credit).
export const financialBillingStates = (batch: FinancialImport): Map<string, FinancialBillingState> => {
  const cached = billingCache.get(batch)
  if (cached) return cached
  const latest = new Map<string, FinancialBilledSnapshot | null>()
  for (const record of batch.exports)
    if (record.kind !== 'review') for (const id of record.rowIds ?? []) latest.set(id, record.snapshot?.[id] ?? null)
  const rows = new Map(batch.charges.map((row) => [row.id, row]))
  const states = new Map<string, FinancialBillingState>()
  for (const [id, snapshot] of latest) {
    const row = rows.get(id)
    const same =
      !snapshot ||
      (row?.review?.status === 'approved' &&
        row.review.customerPrice === snapshot.customerPrice &&
        financialBillingId(row.match) === snapshot.billingId &&
        (!snapshot.itemId || row.match?.itemId === snapshot.itemId) &&
        (!snapshot.currency || batch.currency === snapshot.currency))
    states.set(id, same ? 'billed' : 'changed')
  }
  billingCache.set(batch, states)
  return states
}

export const financialBilledRowIds = (batch: FinancialImport): Set<string> => {
  return new Set([...financialBillingStates(batch)].filter(([, state]) => state === 'billed').map(([id]) => id))
}

export const financialBillingState = (batch: FinancialImport, rowId: string): FinancialBillingState => {
  return financialBillingStates(batch).get(rowId) ?? 'none'
}

// Every saved invoice row per carrier shipment number, across all imports in the workspace.
export const financialShipmentIndex = (imports: FinancialImport[]): FinancialShipmentIndex => {
  const index: FinancialShipmentIndex = new Map()
  for (const batch of imports)
    for (const row of batch.charges) {
      if (!row.trackingNumber) continue
      const key = financialShipmentKey(batch.carrier, row.trackingNumber)
      index.set(key, [...(index.get(key) ?? []), { batch, row }])
    }
  return index
}

export const financialShipmentEntries = (
  index: FinancialShipmentIndex,
  batch: FinancialImport,
  row: FinancialCharge,
): FinancialShipmentEntry[] => {
  if (!row.trackingNumber) return []
  return (index.get(financialShipmentKey(batch.carrier, row.trackingNumber)) ?? [])
    .filter((line) => !(line.batch.id === batch.id && line.row.id === row.id))
    .map((line) => ({
      ...line,
      current: line.batch.id === batch.id,
      role: financialRole(line.batch, line.row),
      amount: financialChargeAmount(line.batch, line.row),
      billed: financialBillingState(line.batch, line.row.id) === 'billed',
      changed: financialBillingState(line.batch, line.row.id) === 'changed',
    }))
}

const where = (entry: FinancialShipmentEntry): string =>
  entry.current ? `row ${entry.row.rowNumber} of this invoice` : `invoice ${financialInvoiceLabel(entry.batch)}, row ${entry.row.rowNumber}`

export const financialRowWarnings = (
  batch: FinancialImport,
  row: FinancialCharge,
  entries: FinancialShipmentEntry[],
): FinancialRowWarning[] => {
  const warnings: FinancialRowWarning[] = []
  const role = financialRole(batch, row)
  const amount = financialChargeAmount(batch, row)
  if (row.match?.origin === 'retained' || row.match?.origin === 'workspace')
    warnings.push({ key: 'linked-match', message: row.match.originNote ?? 'Linked through earlier data.', blocking: false })
  if (role === 'base' && row.match && financialLocalDate(row.match.createdAt) < FINANCIAL_ARREARS_FROM)
    warnings.push({
      key: 'prepaid',
      message: `Booked ${financialLocalDate(row.match.createdAt)}, before billing in arrears started (${FINANCIAL_ARREARS_FROM}, demo date). The delivery may already be invoiced in advance: bill only the difference.`,
      blocking: true,
    })
  if (role === 'credit')
    warnings.push({
      key: 'credit',
      message: 'This row is a credit. Enter a negative customer amount to pass it on, or 0 to keep it.',
      blocking: false,
    })
  const duplicate = entries.find(
    (entry) =>
      entry.row.review?.status !== 'excluded' &&
      entry.row.description === row.description &&
      entry.row.date === row.date &&
      entry.amount !== null &&
      amount !== null &&
      financialAmountsEqual(entry.amount, amount),
  )
  if (duplicate)
    warnings.push({
      key: 'duplicate-row',
      message: `The same charge (${row.description}, ${financialAmountLabel(amount, batch.currency)}, ${row.date}) is also on ${where(duplicate)}. This may be a duplicate invoice.`,
      blocking: true,
    })
  if (role !== 'base' || duplicate) return warnings
  const billedBase = entries.find(
    (entry) => entry.role === 'base' && (entry.billed || entry.changed || entry.row.review?.status === 'approved'),
  )
  const sameInvoiceBase = entries.find(
    (entry) => entry.current && entry.role === 'base' && entry.row.review?.status !== 'excluded',
  )
  if (billedBase)
    warnings.push({
      key: 'base-billed',
      message: `Another delivery row for this shipment already has ${billedBase.billed ? 'a billed' : 'an approved'} customer amount (${financialAmountLabel(billedBase.row.review?.customerPrice ?? null, billedBase.batch.currency)}) on ${where(billedBase)}. Check that the customer is not billed twice.`,
      blocking: true,
    })
  else if (sameInvoiceBase)
    warnings.push({
      key: 'several-base',
      message: `This invoice has another delivery row for this shipment (${where(sameInvoiceBase)}). Bill the delivery once: approve one row and exclude or credit the other.`,
      blocking: true,
    })
  else if (entries.some((entry) => !entry.current && entry.role === 'base'))
    warnings.push({
      key: 'later-fee',
      message: `This shipment already has a delivery row on ${where(entries.find((entry) => !entry.current && entry.role === 'base')!)}. This row may be a later fee: enter only the additional amount.`,
      blocking: true,
    })
  return warnings
}

export const financialRowSignals = (
  batch: FinancialImport,
  row: FinancialCharge,
  index: FinancialShipmentIndex,
  mappings: FinancialServiceMapping[] = [],
): FinancialRowSignals => {
  const entries = financialShipmentEntries(index, batch, row)
  const weight = financialWeightComparison(batch, row)
  const invoiceGrams = financialWeightGrams(batch, row)
  const bookedGrams = row.match?.weightGrams ?? null
  const amount = financialChargeAmount(batch, row)
  const customer = row.review?.status === 'approved' ? row.review.customerPrice : null
  return {
    role: financialRole(batch, row),
    weight,
    service: financialServiceComparison(batch, row, mappings),
    booked: financialBookedComparison(batch, row),
    weightDelta:
      weight.state === 'different' && invoiceGrams !== null && bookedGrams !== null ? invoiceGrams - bookedGrams : null,
    entries,
    warnings: financialRowWarnings(batch, row, entries),
    margin: customer !== null && amount !== null ? subtractFinancialAmounts(customer, amount) : null,
    billed: financialBillingState(batch, row.id) === 'billed',
    changed: financialBillingState(batch, row.id) === 'changed',
  }
}

// Rows that can be approved together at their PacSort price. A loss, a different service, credits and possible double
// billing stay individual. A measured weight difference alone does not: the loss check shows when it cost money.
export const financialBulkBlocker = (
  batch: FinancialImport,
  row: FinancialCharge,
  signals: FinancialRowSignals,
): string | null => {
  if (!financialMetadataComplete(batch)) return 'Invoice details are missing'
  if (row.matchStatus !== 'matched' || !row.match) return 'No unique shipment'
  if (financialChargeAmount(batch, row) === null) return 'Invalid invoice amount'
  if (signals.billed) return 'Already billed'
  if (signals.changed) return 'Changed after billing'
  if (row.review?.status === 'excluded') return 'Excluded'
  if (row.review?.status === 'approved') return 'Already approved'
  if (signals.role === 'credit') return 'Credit row'
  if (signals.booked.state === 'different' || financialLoss(batch, row)) return 'Cost above the customer price'
  if (signals.service.state === 'different') return 'Service differs'
  if (signals.warnings.some((warning) => warning.blocking)) return 'Shipment needs a check'
  return null
}
